import { Link, createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useApi } from '../api/ApiProvider';
import { type AnswerCitation, HelperError } from '../api/helper';
import { useAskStream, useHelperClient, useHelperStatus } from '../api/helper-queries';
import { useMe } from '../api/queries';
import { rootRoute } from './__root';

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Dashboard,
});

function Dashboard() {
  const client = useApi();
  const me = useMe(client);
  const helper = useHelperClient();
  const helperStatus = useHelperStatus();

  return (
    <section style={{ maxWidth: 900 }}>
      <h1 style={{ marginTop: 0 }}>Dashboard</h1>

      <h2>Account</h2>
      {me.isLoading && <p>Loading…</p>}
      {me.isError && <p style={{ color: 'crimson' }}>Not signed in.</p>}
      {me.data && (
        <p>
          Signed in as <code>{(me.data as { email?: string }).email ?? 'unknown'}</code>.
        </p>
      )}

      <h2>Helper</h2>
      {!helper && (
        <p>
          The helper isn't paired in this browser yet. <Link to="/pair">Pair it now →</Link>
        </p>
      )}
      {helperStatus.isLoading && <p>Querying helper…</p>}
      {helperStatus.isError && (
        <div
          style={{
            padding: 12,
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 6,
            color: '#92400e',
          }}
        >
          <strong>Helper unreachable.</strong> The stored credentials may point at a port that no
          longer exists (the helper picks a new ephemeral port on each restart). Try{' '}
          <Link to="/pair">re-pairing</Link>. Underlying error:{' '}
          <code>{describeError(helperStatus.error)}</code>
        </div>
      )}
      {helperStatus.data && (
        <ul>
          <li>
            Version: <code>{helperStatus.data.version}</code>
          </li>
          <li>
            Region: <code>{helperStatus.data.data_region}</code>
          </li>
          <li>
            Indexed files: <code>{helperStatus.data.indexing.files_indexed}</code> (errored:{' '}
            <code>{helperStatus.data.indexing.files_errored}</code>)
          </li>
        </ul>
      )}

      {helper && <AskPanel />}
    </section>
  );
}

function AskPanel() {
  const [question, setQuestion] = useState('');
  const ask = useAskStream();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    void ask.start({ question: q, k: 5 });
  };

  const hasContent = ask.streaming || ask.text || ask.citations.length > 0;

  return (
    <div style={{ marginTop: 16 }}>
      <h2>Ask</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        Ask a question; the helper retrieves the most relevant chunks from your indexed files and
        grounds the answer in them.
      </p>
      <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="What was the team's decision on the export pipeline?"
          style={{ flex: 1 }}
        />
        {ask.streaming ? (
          <button type="button" onClick={() => ask.stop()}>
            Stop
          </button>
        ) : (
          <button type="submit" disabled={!question.trim()}>
            Ask
          </button>
        )}
      </form>

      {ask.error ? <p style={{ color: 'crimson' }}>{describeError(ask.error)}</p> : null}
      {hasContent && (
        <div>
          <div
            style={{
              padding: 12,
              background: '#f6f6f6',
              borderRadius: 6,
              whiteSpace: 'pre-wrap',
              minHeight: 24,
            }}
          >
            {ask.text}
            {ask.streaming && <span style={{ color: '#666' }}>▍</span>}
          </div>
          {ask.model && (
            <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
              via <code>{ask.model}</code>
            </div>
          )}
          {ask.citations.length > 0 && (
            <>
              <h3 style={{ marginBottom: 6 }}>Citations</h3>
              <ol style={{ paddingLeft: 18 }}>
                {ask.citations.map((c, i) => (
                  <li key={c.chunk_id} style={{ marginBottom: 8 }}>
                    <CitationItem index={i + 1} citation={c} />
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CitationItem({ index, citation }: { index: number; citation: AnswerCitation }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <div>
        <code>
          [{index}] {citation.relative_path}
        </code>{' '}
        <span style={{ color: '#666' }}>
          (score {citation.score.toFixed(3)}, trust: {citation.trust_origin})
        </span>{' '}
        <button type="button" onClick={() => setExpanded((v) => !v)} style={{ marginLeft: 4 }}>
          {expanded ? 'Hide excerpt' : 'Show excerpt'}
        </button>
      </div>
      {expanded && (
        <pre
          style={{
            background: '#fafafa',
            padding: 8,
            borderRadius: 4,
            whiteSpace: 'pre-wrap',
            margin: '6px 0 0',
            fontSize: 12,
          }}
        >
          {citation.excerpt}
        </pre>
      )}
    </div>
  );
}

function describeError(e: unknown): string {
  if (e instanceof HelperError) {
    return `${e.problem?.title ?? 'helper error'} (${e.status})${e.problem?.detail ? `: ${e.problem.detail}` : ''}`;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
